import clsx from 'clsx';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    clipboard,
    Description,
    type IconDefinition,
    Label,
    renderIcon,
    Tooltip,
    Truncatable,
} from '@gitbook/spine-react';
import { useAutoFocus, useMergedRefs } from '@gitbook/util-react-hooks';

import { FONT_SIZES, FONT_WEIGHTS, flex, font, sizes, spacing } from '../constants';
import { type GestureHandlers, Interactive } from '../Interactive';
import { ThemedStyleSheet, themedFonts, useCurrentTheme } from '../theme';
import { normalizeStyle, type StyleProp, StyleSheet, type TextStyle, type ViewStyle } from '../web';

export type TextInputSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
export type TextInputKind = 'default' | 'light' | 'transparent' | 'inverted' | 'search';
export type TextInputFeedbackType = 'none' | 'success' | 'info' | 'danger' | 'warning';

export interface TextInputFeedback {
    type: TextInputFeedbackType;
    message?: React.ReactNode;
}

export type TextInputProps = GestureHandlers &
    ({
        value?: string | undefined;
        onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
        onSubmit?: () => void;
        onClear?: () => void;
        label?: string;
        labelDescription?: React.ReactNode;
        labelSelectable?: boolean;
        placeholder?: string;
        size?: TextInputSize;
        kind?: TextInputKind;
        icon?: IconDefinition;
        cleareable?: boolean;
        copyable?: boolean;
        autoWidth?: boolean;
        multiline?: boolean;
        disabled?: boolean;
        autoFocus?: boolean;
        autoComplete?: string;
        secureTextEntry?: boolean;
        selectTextOnFocus?: boolean;
        numberOfLines?: number;
        maxLength?: number;
        title?: React.ReactNode;
        style?: StyleProp<ViewStyle>;
        textStyle?: StyleProp<TextStyle>;
        inputWrapperStyle?: StyleProp<TextStyle>;
        noWrapperFlexGrow?: boolean;
        placeholderTextColor?: string;
        testID?: string;
        /**
         * Button to show on the right side of the input
         */
        button?: React.ReactNode;
        /**
         * React reference for the wrapper of the native input.
         */
        wrapperRef?: React.RefObject<HTMLDivElement | null>;
        /**
         * Text displayed right before the input.
         */
        prefix?: string;
        /**
         * Text displayed right after the input.
         */
        suffix?: string;
        feedback?: TextInputFeedback;
    } & (
        | {
              onChangeText: (value: string) => void;
              readOnly?: false;
          }
        | { onChangeText?: (value: string) => void; readOnly?: true }
    ));

export type TextInputRef = HTMLInputElement | HTMLTextAreaElement;

/**
 * Hook to check if the fonts are loaded.
 */
function useAreFontsLoaded(): boolean {
    const [areFontsLoaded, setAreFontsLoaded] = React.useState(
        'fonts' in document ? document.fonts.status === 'loaded' : true
    );

    React.useEffect(() => {
        if (areFontsLoaded) {
            return;
        }
        let canceled = false;
        document.fonts.ready.then(() => {
            if (!canceled) {
                setAreFontsLoaded(true);
            }
        });
        return () => {
            canceled = true;
        };
    }, [areFontsLoaded]);

    return areFontsLoaded;
}

/**
 * Hook to measure the width of a text with certain styles.
 */
function useMeasureText(input: { text: string; style: TextStyle; skip: boolean }) {
    const fontsLoaded = useAreFontsLoaded();
    const skip = !fontsLoaded || input.skip;
    const { text, style } = input;
    return React.useMemo(() => {
        if (skip) {
            return undefined;
        }

        return measureText(text, style);
    }, [text, style, skip]);
}

/**
 * Component for a basic text input.
 */
export function TextInput({
    ref: forwardedRef,
    ...props
}: TextInputProps & { ref?: React.Ref<TextInputRef> }): React.ReactElement {
    const {
        label,
        labelDescription,
        labelSelectable,
        prefix,
        suffix,
        value,
        style,
        textStyle,
        inputWrapperStyle,
        icon,
        title,
        size = 'large',
        kind = 'default',
        button,
        cleareable = false,
        copyable = false,
        readOnly = false,
        autoWidth = false,
        placeholder,
        multiline = false,
        selectTextOnFocus = false,
        autoFocus = false,
        disabled = false,
        numberOfLines,
        maxLength,
        onChangeText = () => {},
        onSubmit,
        onKeyPress,
        onClear,
        testID,
        placeholderTextColor,
        feedback = { type: 'none' },
        wrapperRef,
        autoComplete,
        secureTextEntry = false,
        noWrapperFlexGrow,
        ...eventHandlers
    } = props;
    // @ts-ignore: error raised by the addition of the `disabled` style which works
    const [theme, kindStyles] = useCurrentTheme(kindStylesTheme[kind]);
    const [, font] = useCurrentTheme(themedFonts);
    const localInputRef = React.useRef<TextInputRef>(null);
    const inputRef = useMergedRefs(forwardedRef, localInputRef);
    useAutoFocus(localInputRef, autoFocus);

    const onPressClear = () => {
        onChangeText('');
        if (onClear) {
            onClear();
        }
    };

    const onNativeKeyPress = (
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (event.key === 'Enter' && (!event.shiftKey || !multiline) && onSubmit) {
            onSubmit();
            return;
        }

        if (onKeyPress) {
            onKeyPress(event);
        }
    };

    const nativeTextStyles = React.useMemo(
        () =>
            StyleSheet.flatten([
                styles.textInputNative,
                kindStyles.inputNative,
                textInputFontSizes[size],
                textStyle,
            ]),
        [kindStyles.inputNative, size, textStyle]
    );

    const width = useMeasureText({
        text: value || placeholder || '',
        style: nativeTextStyles ?? {},
        skip: !autoWidth,
    });
    const nativeInputStyle = {
        ...(normalizeStyle([
            nativeTextStyles,
            width === undefined ? {} : { width },
            readOnly ? ({ cursor: 'default' } as ViewStyle) : {},
        ]) ?? {}),
        '--placeholder-color': placeholderTextColor || theme.colors.text.light,
    } as React.CSSProperties;

    const FeedbackIcon =
        feedback.type === 'success'
            ? Icon.Check
            : feedback.type === 'danger' || feedback.type === 'warning'
              ? Icon.Alert
              : undefined;
    const feedbackStyle =
        feedback.type === 'success'
            ? 'text-success'
            : feedback.type === 'info'
              ? 'text-primary'
              : feedback.type === 'danger'
                ? 'text-danger'
                : 'text-danger';

    return (
        <Tooltip tooltip={title} disabled={!title}>
            <div className={clsx('flex flex-col', !noWrapperFlexGrow && 'grow')}>
                <Interactive.WithInteractiveState {...eventHandlers}>
                    {({ hovered, focused, domEvents }) => (
                        <div ref={wrapperRef} style={normalizeStyle(style)}>
                            {label ? (
                                <div className="mb-2">
                                    <div>
                                        <Label
                                            className={labelSelectable ? 'select-all' : undefined}
                                        >
                                            {label}
                                        </Label>
                                    </div>
                                    {labelDescription ? (
                                        <div className="mt-1">
                                            <Description
                                                className={
                                                    labelSelectable ? 'select-all' : undefined
                                                }
                                            >
                                                {labelDescription}
                                            </Description>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            <div
                                className="flex items-center"
                                style={normalizeStyle([
                                    flex.row,
                                    flex.alignItemsCenter,
                                    kindStyles.input,
                                    feedback.type === 'danger' && {
                                        borderColor: theme.colors.danger.main,
                                    },
                                    feedback.type === 'warning' && {
                                        borderColor: theme.colors.warning.main,
                                    },
                                    hovered && !readOnly && kindStyles.hovered,
                                    focused && kindStyles.focused,
                                    wrapperSizeStyles[size],
                                    textInputSizeStyles[size],
                                    multiline && styles.textInputMultline,
                                    inputWrapperStyle,
                                    disabled && kindStyles.disabled,
                                ])}
                            >
                                {prefix ? (
                                    <Truncatable
                                        className={clsx(
                                            'min-w-0 shrink text-muted',
                                            prefixSizeClasses[size]
                                        )}
                                    >
                                        {prefix}
                                    </Truncatable>
                                ) : null}
                                <div
                                    className="flex min-w-0 flex-1 items-center"
                                    style={normalizeStyle([
                                        flex.flex1,
                                        flex.alignItemsCenter,
                                        styles.wrapper,
                                    ])}
                                >
                                    {icon
                                        ? renderIcon(icon, {
                                              className: clsx(
                                                  'mr-2 shrink-0',
                                                  !focused && 'text-muted'
                                              ),
                                          })
                                        : null}
                                    {multiline ? (
                                        <textarea
                                            className="m-0 bg-transparent p-0 placeholder:text-(--placeholder-color)"
                                            ref={inputRef as React.Ref<HTMLTextAreaElement>}
                                            value={value}
                                            readOnly={readOnly}
                                            disabled={disabled}
                                            rows={numberOfLines}
                                            maxLength={maxLength}
                                            autoComplete={autoComplete}
                                            placeholder={placeholder}
                                            autoFocus={autoFocus}
                                            {...domEvents}
                                            onChange={(event) =>
                                                onChangeText(event.currentTarget.value)
                                            }
                                            onKeyDown={onNativeKeyPress}
                                            onFocus={(event) => {
                                                if (selectTextOnFocus) {
                                                    event.currentTarget.select();
                                                }
                                                domEvents.onFocus(event);
                                            }}
                                            style={nativeInputStyle}
                                            data-testid={testID}
                                        />
                                    ) : (
                                        <input
                                            className="m-0 bg-transparent p-0 placeholder:text-(--placeholder-color)"
                                            ref={inputRef as React.Ref<HTMLInputElement>}
                                            type={secureTextEntry ? 'password' : 'text'}
                                            value={value}
                                            readOnly={readOnly}
                                            disabled={disabled}
                                            maxLength={maxLength}
                                            autoComplete={autoComplete}
                                            placeholder={placeholder}
                                            autoFocus={autoFocus}
                                            {...domEvents}
                                            onChange={(event) =>
                                                onChangeText(event.currentTarget.value)
                                            }
                                            onKeyDown={onNativeKeyPress}
                                            onFocus={(event) => {
                                                if (selectTextOnFocus) {
                                                    event.currentTarget.select();
                                                }
                                                domEvents.onFocus(event);
                                            }}
                                            style={nativeInputStyle}
                                            data-testid={testID}
                                        />
                                    )}
                                    {FeedbackIcon && (
                                        <FeedbackIcon
                                            size={18}
                                            className={clsx(
                                                (cleareable && value && value.length > 0) || button
                                                    ? 'mr-4'
                                                    : '',
                                                feedbackStyle
                                            )}
                                        />
                                    )}
                                    {cleareable && value && value.length > 0 && (
                                        <Button
                                            leadingIcon={Icon.X}
                                            iconOnly
                                            onPress={onPressClear}
                                            label="Clear"
                                            size="small"
                                            kind="blank"
                                        />
                                    )}
                                    {button ? <div className="ml-2">{button}</div> : null}
                                </div>
                                {suffix ? (
                                    <div className="ml-2">
                                        <span
                                            style={normalizeStyle([
                                                textInputFontSizes[size],
                                                font.textMid,
                                            ])}
                                        >
                                            {suffix}
                                        </span>
                                    </div>
                                ) : null}
                                {copyable && value?.length ? (
                                    <Button
                                        size="small"
                                        iconOnly
                                        leadingIcon={Icon.Copy}
                                        label="Copy"
                                        kind="blank"
                                        onPress={() => clipboard.writeText(value)}
                                    />
                                ) : null}
                            </div>
                            {feedback.type !== 'none' &&
                                typeof feedback.message !== 'undefined' && (
                                    <div className={clsx('pt-2 text-ui-small', feedbackStyle)}>
                                        {feedback.message}
                                    </div>
                                )}
                        </div>
                    )}
                </Interactive.WithInteractiveState>
            </div>
        </Tooltip>
    );
}

/**
 * Measure the width of a text with certain styles.
 */
export function measureText(text: string, style: TextStyle): number {
    canvas = canvas || document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('failed to create 2d canvas context');
    }

    context.font = `${style.fontWeight || FONT_WEIGHTS.medium} ${
        style.fontSize || FONT_SIZES.small
    }px ${style.fontFamily || 'gitbook-content-font'}`;
    const metrics = context.measureText(text);

    return (
        metrics.width +
        getPadding(style.paddingLeft) +
        getPadding(style.paddingRight) +
        getPadding(style.paddingHorizontal) * 2
    );
}

let canvas: HTMLCanvasElement | null = null;
function getPadding(pad: string | number | undefined) {
    if (typeof pad === 'number') {
        return pad;
    }
    if (typeof pad === 'string') {
        return Number.parseFloat(pad);
    }
    return 0;
}

const styles = StyleSheet.create({
    wrapper: {
        // flex 1 to ensure that it takes the remaining space after
        // pieces like "suffix" and "prefix" have gotten space to render themselves
        // otherwise they will bleed out
        flex: 1,
        display: 'flex',
        border: 'none',
        flexDirection: 'row',
        flexBasis: 'auto',
        alignItems: 'center',
    },
    textInputNative: {
        flex: 1,
        outlineStyle: 'none',
        paddingVertical: spacing.small,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    textInputMultline: {
        height: 'auto',
    },
    textInputIcon: {
        flexShrink: 0,
        marginRight: spacing.small,
    },
    feedbackIcon: {
        marginRight: spacing.medium,
    },
    textInputFeedback: {
        paddingTop: spacing.small,
    },
});

/**
 * Font sizes to use depending on the text input size.
 */
export const textInputFontSizes: { [size in TextInputSize]: TextStyle } = {
    xsmall: font.Caption,
    small: font.Caption,
    medium: font.Label,
    large: font.Label,
    xlarge: font.Label,
    xxlarge: font.Label,
};

const prefixSizeClasses: Record<TextInputSize, string> = {
    xsmall: 'text-ui-small',
    small: 'text-ui-small',
    medium: 'text-ui-action',
    large: 'text-ui-action',
    xlarge: 'text-ui-action',
    xxlarge: 'text-ui-action',
};

const wrapperSizeStyles: { [size in TextInputSize]: ViewStyle } = StyleSheet.create({
    xsmall: {
        borderRadius: sizes.RADIUS_M,
    },
    small: {
        borderRadius: sizes.RADIUS_M,
    },
    medium: {
        borderRadius: sizes.RADIUS_M,
    },
    large: {
        borderRadius: sizes.RADIUS_L,
    },
    xlarge: {
        borderRadius: sizes.RADIUS_XL,
    },
    xxlarge: {
        borderRadius: sizes.RADIUS_XL,
    },
});

/** Sizes of the inner text input, taking in consideration the wrapper with the border. */
export const textInputSizeStyles: { [size in TextInputSize]: ViewStyle } = StyleSheet.create({
    xsmall: {
        height: sizes.BUTTON_XS,
        borderRadius: sizes.RADIUS_M,
        paddingHorizontal: spacing.xxsmall,
    },
    small: {
        height: sizes.BUTTON_S,
        borderRadius: sizes.RADIUS_M,
        paddingHorizontal: spacing.small,
    },
    medium: {
        height: sizes.BUTTON_M,
        borderRadius: sizes.RADIUS_M,
        paddingHorizontal: 12, // Same as the Spine input
    },
    large: {
        height: sizes.BUTTON_L,
        borderRadius: sizes.RADIUS_M,
        paddingHorizontal: spacing.medium,
    },
    xlarge: {
        height: sizes.BUTTON_XL,
        borderRadius: sizes.RADIUS_XL,
        paddingHorizontal: spacing.large,
    },
    xxlarge: {
        height: sizes.L,
        borderRadius: sizes.RADIUS_XL,
        paddingHorizontal: spacing.large,
    },
});

const kindStylesTheme = {
    default: ThemedStyleSheet.create({
        input: (theme) => ({
            borderWidth: 1,
            borderColor: theme.colors.border.main,
            backgroundColor: theme.colors.background.main,
        }),
        inputNative: (theme) => ({
            color: theme.colors.text.main,
        }),
        hovered: {},
        focused: (theme) => ({
            borderColor: theme.colors.border.secondary,
        }),
        disabled: {
            backgroundColor: 'var(--sp-color-bg-muted)',
            cursor: 'not-allowed',
        },
    }),
    light: ThemedStyleSheet.create({
        input: {
            borderColor: 'transparent',
        },
        inputNative: (theme) => ({
            color: theme.colors.text.main,
        }),
        hovered: (theme) => ({
            backgroundColor: theme.colors.itemHover,
        }),
        focused: (theme) => ({
            backgroundColor: theme.colors.itemHover,
        }),
    }),
    transparent: ThemedStyleSheet.create({
        input: {
            borderWidth: 0,
            borderColor: 'transparent',
        },
        inputNative: (theme) => ({
            color: theme.colors.text.main,
        }),
        hovered: {
            backgroundColor: 'transparent',
        },
        focused: {
            backgroundColor: 'transparent',
        },
    }),
    search: ThemedStyleSheet.create({
        input: (theme) => ({
            borderWidth: 1,
            borderColor: theme.colors.border.mid,
            backgroundColor: theme.colors.background.main,
        }),
        inputNative: (theme) => ({
            color: theme.colors.text.main,
        }),
        hovered: (theme) => ({
            borderColor: theme.colors.border.main,
        }),
        focused: (theme) => ({
            borderColor: theme.colors.border.main,
        }),
    }),
    inverted: ThemedStyleSheet.create({
        input: (theme) => ({
            borderWidth: 0,
            borderColor: 'transparent',
            backgroundColor: theme.colors.background.inverted,
        }),
        inputNative: (theme) => ({
            color: theme.colors.text.inverted,
        }),
        hovered: (theme) => ({
            backgroundColor: theme.colors.background.inverted,
        }),
        focused: (theme) => ({
            backgroundColor: theme.colors.background.inverted,
        }),
    }),
};
